import DeleteIcon from '@mui/icons-material/Delete';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { userApis } from '../../../apis/userApis';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { formatDateTimeUTC } from '../../../utils/timeUtils';
import { sentenceCase } from '../../../utils/utils';
import AlertMessage from '../../elements/alert-message/AlertMessage';
import Table from '../../elements/table/Table';
import DeleteButton from '../../sections/delete/DeleteButton';
import UserAvatar from '../../sections/user-avatar/UserAvatar';

const CidrTable = ({ cidrs, instanceBusy, onRemoveCidr }) => {
  const { showToast } = useToast();
  const { t } = useTranslation();

  if (instanceBusy)
    return (
      <AlertMessage
        message={t('instanceIsBusy', { ns: 'descriptions' })}
        severity="info"
        style={{ marginTop: '0.5em', marginBottom: '0.5em' }}
      />
    );

  const handleRemoveCidr = async (cidr) => {
    try {
      const filteredCidrs = cidrs.filter((c) => c.cidr !== cidr);
      await onRemoveCidr(filteredCidrs);
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('updateSuccess', {
            ns: 'notifications',
            text: t('instanceInfo', { ns: 'instance' }),
          })
        )
      );
    } catch {
      showToast(
        TOAST_TYPE.ERROR,
        sentenceCase(
          t('updateError', {
            ns: 'notifications',
            text: t('instanceInfo', { ns: 'instance' }),
          })
        )
      );
    }
  };

  const tableRows = cidrs.map((c) => ({
    ipRange: c.cidr,
    creator: <UserAvatar user={c.meta.createUser} />,
    createDate: formatDateTimeUTC(c.meta.createDate, userApis?.timeZone),
    actions: (
      <DeleteButton
        buttonProps={{
          variant: 'outlined',
          startIcon: <DeleteIcon />,
          size: 'small',
        }}
        description={t('deleteIpRange', {
          ns: 'descriptions',
          text: c.cidr,
        })}
        onDelete={() => handleRemoveCidr(c.cidr)}
        title={t('deletion', {
          ns: 'project',
          text: t('ipRange', { ns: 'fields' }),
        })}
        validation={c.cidr}
      />
    ),
  }));

  const tableHeaders = {
    ipRange: {
      name: t('ipRange', { ns: 'fields' }),
      sortable: true,
    },
    creator: {
      name: t('creator', { ns: 'instance' }),
      sortable: false,
    },
    createDate: {
      name: t('createDate', { ns: 'instance' }),
      sortable: true,
    },
    actions: {
      name: '',
      sortable: false,
    },
  };

  return (
    <Table
      small
      headers={tableHeaders}
      rows={tableRows}
      emptyText={t('noIpSet', { ns: 'descriptions' })}
    />
  );
};

CidrTable.propTypes = {
  cidrs: PropTypes.array.isRequired,
  instanceBusy: PropTypes.bool,
  minimal: PropTypes.bool,
  onRemoveCidr: PropTypes.func,
};

export default CidrTable;
